// v1
// Path: frontend/src/components/SyncHub/shared/MappingDesignerTable.jsx
// Inputs: numerous props from FieldMappingDesigner
// Outputs: The main grid for mapping source fields to target fields.
// Summary: Extracted mapping grid to split up FieldMappingDesigner.

import React from 'react';

export default function MappingDesignerTable(props) {
    const {
        isStandardMode,
        searchFilter,
        setSearchFilter,
        selectedQBEntity,
        qbSampleRecords,
        activeQbSampleIndex,
        setActiveQbSampleIndex,
        pbsSampleRecords,
        activeSampleIndex,
        setActiveSampleIndex,
        activeTableName,
        filteredMappings,
        definitions,
        newTarget,
        setNewTarget,
        newSource,
        setNewSource,
        handleAddCustomMapping,
        handleCustomMappingChange,
        handleRemoveCustomRow
    } = props;

    // Helper inside render
    const getQbSampleValue = (fieldPath) => {
        if (!qbSampleRecords || qbSampleRecords.length === 0) return null;
        const rec = qbSampleRecords[activeQbSampleIndex];
        if (!rec) return null;
        const parts = fieldPath.split('.');
        let val = rec;
        for (const p of parts) {
            if (val == null) break;
            val = val[p];
        }
        return val;
    };

    const getPbsSampleValue = (fieldNames, template) => {
        if (!pbsSampleRecords || pbsSampleRecords.length === 0) return null;
        const rec = pbsSampleRecords[activeSampleIndex];
        if (!rec) return null;
        if (template) {
            let str = template;
            fieldNames.forEach(f => {
                const regex = new RegExp(`\\{${f}\\}`, 'g');
                str = str.replace(regex, rec[f] !== undefined ? String(rec[f]).trim() : '');
            });
            return str;
        }
        return fieldNames.map(f => rec[f] !== undefined ? String(rec[f]).trim() : '').join(' ').trim();
    };

    return (
        <div className="flex-1 overflow-y-auto flex flex-col bg-slate-900">
            {/* Control Bar */}
            <div className="flex items-center justify-between p-3 bg-slate-800 border-b border-slate-700 shrink-0">
                <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-slate-400">filter_alt</span>
                    <input
                        type="text"
                        value={searchFilter}
                        onChange={e => setSearchFilter(e.target.value)}
                        placeholder="Filter target or source..."
                        className="bg-slate-950 border border-slate-700 rounded px-2.5 py-1 text-white text-xs focus:border-indigo-500 focus:outline-none w-48"
                    />
                </div>
            </div>

            {/* Live Sample Navigators - Hidden in Standard Mode */}
            {!isStandardMode && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-slate-950/60 border-b border-slate-800 shrink-0 text-xs">
                    {/* LEFT: QuickBooks Sample */}
                    <div className="bg-slate-900/90 border border-purple-900/50 rounded-xl p-3 space-y-2">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-1.5 font-bold text-purple-300">
                                <span className="material-symbols-outlined text-[16px]">cloud</span>
                                <span>Target Sample ({selectedQBEntity})</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => setActiveQbSampleIndex(0)}
                                    disabled={activeQbSampleIndex === 0 || qbSampleRecords.length === 0}
                                    className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                >&laquo;</button>
                                <button
                                    type="button"
                                    onClick={() => setActiveQbSampleIndex(Math.max(0, activeQbSampleIndex - 1))}
                                    disabled={activeQbSampleIndex === 0 || qbSampleRecords.length === 0}
                                    className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                >&lsaquo;</button>
                                <span className="text-slate-400 mx-1">{qbSampleRecords.length > 0 ? activeQbSampleIndex + 1 : 0} / {qbSampleRecords.length}</span>
                                <button
                                    type="button"
                                    onClick={() => setActiveQbSampleIndex(Math.min(qbSampleRecords.length - 1, activeQbSampleIndex + 1))}
                                    disabled={activeQbSampleIndex >= qbSampleRecords.length - 1 || qbSampleRecords.length === 0}
                                    className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                >&rsaquo;</button>
                                <button
                                    type="button"
                                    onClick={() => setActiveQbSampleIndex(qbSampleRecords.length - 1)}
                                    disabled={activeQbSampleIndex >= qbSampleRecords.length - 1 || qbSampleRecords.length === 0}
                                    className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                >&raquo;</button>
                            </div>
                        </div>
                    </div>
                    {/* RIGHT: Source Sample */}
                    <div className="bg-slate-900/90 border border-emerald-900/50 rounded-xl p-3 space-y-2">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-1.5 font-bold text-emerald-400">
                                <span className="material-symbols-outlined text-[16px]">database</span>
                                <span>Source Sample ({activeTableName})</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => setActiveSampleIndex(0)}
                                    disabled={activeSampleIndex === 0 || pbsSampleRecords.length === 0}
                                    className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                >&laquo;</button>
                                <button
                                    type="button"
                                    onClick={() => setActiveSampleIndex(Math.max(0, activeSampleIndex - 1))}
                                    disabled={activeSampleIndex === 0 || pbsSampleRecords.length === 0}
                                    className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                >&lsaquo;</button>
                                <span className="text-slate-400 mx-1">{pbsSampleRecords.length > 0 ? activeSampleIndex + 1 : 0} / {pbsSampleRecords.length}</span>
                                <button
                                    type="button"
                                    onClick={() => setActiveSampleIndex(Math.min(pbsSampleRecords.length - 1, activeSampleIndex + 1))}
                                    disabled={activeSampleIndex >= pbsSampleRecords.length - 1 || pbsSampleRecords.length === 0}
                                    className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                >&rsaquo;</button>
                                <button
                                    type="button"
                                    onClick={() => setActiveSampleIndex(pbsSampleRecords.length - 1)}
                                    disabled={activeSampleIndex >= pbsSampleRecords.length - 1 || pbsSampleRecords.length === 0}
                                    className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                >&raquo;</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Mappings Table Grid */}
            <div className="flex-1 overflow-y-auto">
                <table className="w-full text-left text-xs border-collapse">
                    <thead className="sticky top-0 bg-slate-900 border-b border-slate-700 z-10 shadow-sm">
                        <tr>
                            <th className="px-4 py-3 font-semibold text-slate-300 w-1/4">Target Field (QuickBooks)</th>
                            <th className="px-4 py-3 font-semibold text-slate-300 w-1/2 border-l border-slate-800">Source Builder (Passport)</th>
                            <th className="px-4 py-3 font-semibold text-slate-300 w-16 text-center border-l border-slate-800">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/50">
                        {filteredMappings.map((mapping, idx) => {
                            const isReq = (definitions?.targetFields || []).find(t => t.name === mapping.target)?.required;
                            const isCustom = !((definitions?.targetFields || []).find(t => t.name === mapping.target));
                            const qbVal = isStandardMode ? null : getQbSampleValue(mapping.target);
                            const pbsVal = isStandardMode ? null : getPbsSampleValue(mapping.sources || [], mapping.template);

                            return (
                                <tr key={`${mapping.target}-${idx}`} className="hover:bg-slate-800/20 group transition-colors">
                                    <td className="px-4 py-3 align-top">
                                        <div className="flex items-center gap-2">
                                            {isReq && <span className="text-rose-500 font-bold" title="Required">*</span>}
                                            {isCustom && <span className="px-1 py-0.5 rounded bg-indigo-900 text-indigo-300 text-[9px] font-bold">CUSTOM</span>}
                                            <span className="font-mono text-slate-200">{mapping.target}</span>
                                        </div>
                                        {!isStandardMode && (
                                            <div className="mt-2 text-[10px] bg-slate-850 p-1.5 rounded font-mono text-slate-400 overflow-x-auto whitespace-nowrap">
                                                {qbVal !== null && qbVal !== undefined ? (
                                                    <span className="text-purple-300">{typeof qbVal === 'object' ? JSON.stringify(qbVal) : String(qbVal)}</span>
                                                ) : <span className="text-slate-600 italic">null</span>}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 align-top border-l border-slate-800/50">
                                        <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                            {(mapping.sources || []).map((src, sIdx) => (
                                                <div key={sIdx} className="flex items-center gap-1 bg-slate-800 border border-slate-700 text-emerald-400 px-2 py-1 rounded-md">
                                                    <span className="font-mono text-[11px]">{src}</span>
                                                    <button type="button" onClick={() => {
                                                        const ns = [...mapping.sources];
                                                        ns.splice(sIdx, 1);
                                                        handleCustomMappingChange(mapping.target, 'sources', ns);
                                                    }} className="text-slate-500 hover:text-rose-400 ml-1">&times;</button>
                                                </div>
                                            ))}
                                            <select
                                                value=""
                                                onChange={(e) => {
                                                    if (!e.target.value) return;
                                                    const ns = [...(mapping.sources || []), e.target.value];
                                                    handleCustomMappingChange(mapping.target, 'sources', ns);
                                                }}
                                                className="bg-slate-850 border border-slate-700 text-slate-300 px-2 py-1 rounded text-[11px] focus:outline-none focus:border-indigo-500"
                                            >
                                                <option value="">+ Add Column</option>
                                                {(definitions?.sourceColumns || []).map(col => (
                                                    <option key={col.COLUMN_NAME} value={col.COLUMN_NAME}>{col.COLUMN_NAME}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {!isStandardMode && mapping.sources?.length > 0 && (
                                            <div className="mt-2 text-[10px] bg-slate-850 p-1.5 rounded font-mono text-emerald-300 overflow-x-auto whitespace-nowrap">
                                                {pbsVal !== null && pbsVal !== undefined && pbsVal !== '' ? pbsVal : <span className="text-slate-600 italic">empty</span>}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 align-middle text-center border-l border-slate-800/50">
                                        <button type="button" onClick={() => handleRemoveCustomRow(mapping.target)} className="text-slate-500 hover:text-rose-400" title="Remove Mapping">
                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        <tr className="bg-slate-900 border-t border-indigo-900/30">
                            <td className="px-4 py-3">
                                <input
                                    type="text"
                                    placeholder="e.g. CustomField.Name"
                                    value={newTarget}
                                    onChange={e => setNewTarget(e.target.value)}
                                    className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-white focus:border-indigo-500 focus:outline-none"
                                />
                            </td>
                            <td className="px-4 py-3 border-l border-slate-800/50">
                                <select
                                    value={newSource}
                                    onChange={e => setNewSource(e.target.value)}
                                    className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-white focus:border-indigo-500 focus:outline-none"
                                >
                                    <option value="">-- Select Source Column --</option>
                                    {(definitions?.sourceColumns || []).map(c => (
                                        <option key={c.COLUMN_NAME} value={c.COLUMN_NAME}>{c.COLUMN_NAME}</option>
                                    ))}
                                </select>
                            </td>
                            <td className="px-4 py-3 text-center border-l border-slate-800/50">
                                <button type="button" onClick={handleAddCustomMapping} disabled={!newTarget || !newSource} className="text-indigo-400 hover:text-indigo-300 disabled:opacity-30">
                                    <span className="material-symbols-outlined">add_circle</span>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}
